import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const CATEGORIES = ["Web Development", "PC Repair", "Automation", "Device Support"];
const empty = { name: "", slug: "", category: "Web Development", shortDescription: "", fullDescription: "", icon: "wrench", startingPrice: 0, features: [], active: true, featured: false, displayOrder: 0, ctaText: "Request Service", ctaDestination: "/request-service", seoTitle: "", seoDescription: "", priceType: "starting_at" };

export default function Services() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: services } = useQuery({ queryKey: ["admin-services"], queryFn: () => api.get("/services").then(r => r.data.services) });

  const del = async (id) => {
    try { await api.delete(`/admin/services/${id}`); qc.invalidateQueries({ queryKey: ["admin-services"] }); qc.invalidateQueries({ queryKey: ["services"] }); toast.success("Service deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <PageTitle title="Services" subtitle="Editable service catalog — updates the public site instantly" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setEditing({ ...empty })} data-testid="new-service-btn"><Plus className="h-4 w-4" /> New Service</Button>
      } />
      <div className="grid md:grid-cols-2 gap-4">
        {(services || []).map((s) => (
          <div key={s.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`service-item-${s.slug}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2"><h3 className="font-heading font-semibold text-navy">{s.name}</h3>{s.featured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-electric/10 text-electric font-mono">FEATURED</span>}</div>
                <p className="text-xs text-slate-500 font-mono">{s.category} · /{s.slug}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(s)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-service-${s.slug}`}><Pencil className="h-4 w-4" /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-service-${s.slug}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete {s.name}?</AlertDialogTitle><AlertDialogDescription>This will remove it from the public site (soft-deleted). This action can be reversed by an admin.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(s.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600">{s.shortDescription}</p>
          </div>
        ))}
      </div>
      {editing && <ServiceDialog service={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-services"] }); qc.invalidateQueries({ queryKey: ["services"] }); setEditing(null); }} />}
    </div>
  );
}

function ServiceDialog({ service, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...service, features: (service.features || []).join("\n") });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, startingPrice: Number(form.startingPrice) || 0, displayOrder: Number(form.displayOrder) || 0, features: form.features.split("\n").map(x => x.trim()).filter(Boolean) };
    try {
      if (service.id) await api.put(`/admin/services/${service.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{service.id ? "Edit" : "New"} Service</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="service-form">
          <input className={input} placeholder="Name" required value={form.name} onChange={e => set("name", e.target.value)} data-testid="svc-name" />
          <input className={input} placeholder="Slug (e.g. web-development)" required value={form.slug} onChange={e => set("slug", e.target.value)} data-testid="svc-slug" />
          <select className={input} value={form.category} onChange={e => set("category", e.target.value)}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          <input className={input} placeholder="Icon (globe, cpu, workflow, smartphone, wrench)" value={form.icon} onChange={e => set("icon", e.target.value)} />
          <input className={input} placeholder="Short description" value={form.shortDescription} onChange={e => set("shortDescription", e.target.value)} data-testid="svc-short" />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} type="number" placeholder="Starting price" value={form.startingPrice} onChange={e => set("startingPrice", e.target.value)} />
            <input className={input} type="number" placeholder="Display order" value={form.displayOrder} onChange={e => set("displayOrder", e.target.value)} />
          </div>
          <textarea className={`${input} h-auto py-2`} rows={4} placeholder="Features (one per line)" value={form.features} onChange={e => set("features", e.target.value)} data-testid="svc-features" />
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} /> Active</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} /> Featured</label>
          </div>
          <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="svc-save">Save Service</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
